import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/Card";
import { Mail, Lock, User, ArrowLeft } from "lucide-react";

type AuthMode = "signin" | "signup" | "reset";

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signIn, signUp, resetPassword, loading, error, clearError } = useAuth();
  const { success, error: toastError } = useToast();

  const [mode, setMode] = useState<AuthMode>(
    (searchParams.get("mode") as AuthMode) || "signup"
  );
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    displayName: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const referralCode = searchParams.get("ref");

  // Validate redirect URL to prevent open redirect vulnerability
  const getSafeRedirectUrl = (redirect: string | null): string => {
    if (!redirect) return "/dashboard";

    // Whitelist of allowed internal redirect paths
    const allowedPaths = [
      "/dashboard",
      "/custom-quote",
      "/summer-camp-quote",
      "/hubstop-quote",
      "/yearly-quote",
      "/routes",
      "/contact",
    ];

    // Check if the redirect starts with a forward slash (internal path)
    // and doesn't contain any protocol (http://, https://, javascript:, etc.)
    if (
      redirect.startsWith("/") &&
      !redirect.includes("://") &&
      !redirect.toLowerCase().startsWith("javascript:") &&
      allowedPaths.some(path => redirect.startsWith(path))
    ) {
      return redirect;
    }

    // If validation fails, default to dashboard
    return "/dashboard";
  };

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const redirectParam = searchParams.get("redirect");
      const redirectTo = getSafeRedirectUrl(redirectParam);
      navigate(redirectTo);
    }
  }, [user, navigate, searchParams]);

  // Clear errors when switching modes
  useEffect(() => {
    clearError();
    setFormErrors({});
  }, [mode, clearError]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Invalid email address";
    }

    if (mode !== "reset") {
      if (!formData.password) {
        errors.password = "Password is required";
      } else if (formData.password.length < 6) {
        errors.password = "Password must be at least 6 characters";
      }
    }

    if (mode === "signup") {
      if (!formData.displayName) {
        errors.displayName = "Name is required";
      }
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      if (mode === "signin") {
        await signIn(formData.email, formData.password);
        success("Welcome back!", "You've been signed in successfully.");
      } else if (mode === "signup") {
        await signUp(formData.email, formData.password, formData.displayName, referralCode || undefined);
        if (referralCode) {
          success("Account created!", "Your referral has been recorded. Please check your email to verify your account.");
        } else {
          success("Account created!", "Please check your email to verify your account.");
        }
      } else if (mode === "reset") {
        await resetPassword(formData.email);
        success("Reset email sent!", "Check your inbox for password reset instructions.");
        setMode("signin");
      }
    } catch (err) {
      toastError("Authentication failed", error || "Please try again.");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 bg-gradient-to-b from-muted/30 to-background">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <Card className="border-border/50">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-shuttlebee-yellow rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg viewBox="0 0 24 24" className="w-8 h-8 text-shuttlebee-dark" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
            </div>
            <CardTitle className="text-2xl">
              {mode === "signin" && "Welcome back"}
              {mode === "signup" && "Create an account"}
              {mode === "reset" && "Reset password"}
            </CardTitle>
            <CardDescription>
              {mode === "signin" && "Sign in to manage your transportation requests"}
              {mode === "signup" && "Get started with Shuttlebee Family Connect"}
              {mode === "reset" && "We'll send you a reset link"}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {mode === "signup" && referralCode && (
              <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800">
                🎁 You're signing up with a referral! You and your referrer will both benefit.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "signup" && (
                <Input
                  label="Full Name"
                  name="displayName"
                  type="text"
                  placeholder="John Smith"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  error={formErrors.displayName}
                  icon={<User className="w-4 h-4" />}
                />
              )}

              <Input
                label="Email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleInputChange}
                error={formErrors.email}
                icon={<Mail className="w-4 h-4" />}
              />

              {mode !== "reset" && (
                <Input
                  label="Password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={formErrors.password}
                  icon={<Lock className="w-4 h-4" />}
                />
              )}

              {mode === "signup" && (
                <Input
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  error={formErrors.confirmPassword}
                  icon={<Lock className="w-4 h-4" />}
                />
              )}

              {error && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                variant="shuttlebee"
                size="lg"
                className="w-full"
                loading={loading}
              >
                {mode === "signin" && "Sign In"}
                {mode === "signup" && "Create Account"}
                {mode === "reset" && "Send Reset Link"}
              </Button>
            </form>

            <div className="mt-6 space-y-4">
              {mode === "signin" && (
                <>
                  <button
                    type="button"
                    onClick={() => setMode("reset")}
                    className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Forgot your password?
                  </button>
                  <div className="text-center text-sm">
                    <span className="text-muted-foreground">Don't have an account? </span>
                    <button
                      type="button"
                      onClick={() => setMode("signup")}
                      className="text-primary hover:underline font-medium"
                    >
                      Sign up
                    </button>
                  </div>
                </>
              )}

              {mode === "signup" && (
                <div className="text-center text-sm">
                  <span className="text-muted-foreground">Already have an account? </span>
                  <button
                    type="button"
                    onClick={() => setMode("signin")}
                    className="text-primary hover:underline font-medium"
                  >
                    Sign in
                  </button>
                </div>
              )}

              {mode === "reset" && (
                <div className="text-center text-sm">
                  <span className="text-muted-foreground">Remember your password? </span>
                  <button
                    type="button"
                    onClick={() => setMode("signin")}
                    className="text-primary hover:underline font-medium"
                  >
                    Sign in
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          By continuing, you agree to our{" "}
          <Link to="/terms" className="underline hover:text-foreground">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="underline hover:text-foreground">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
}
